// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { cn } from '@/lib/utils'

interface YesNoOption {
  value:   string
  label:   string
}

interface YesNoStepProps {
  title:        string
  question:     string
  description?: string
  value:        string
  onChange:     (v: string) => void
  options?:     YesNoOption[]
  conditionKey?: string
  conditionContent?: React.ReactNode
}

const DEFAULT_OPTIONS: YesNoOption[] = [
  { value: 'yes', label: 'Ja'  },
  { value: 'no',  label: 'Nee' },
]

const THREE_OPTIONS: YesNoOption[] = [
  { value: 'yes',     label: 'Ja'           },
  { value: 'no',      label: 'Nee'          },
  { value: 'unknown', label: 'Weet ik niet' },
]

export { DEFAULT_OPTIONS, THREE_OPTIONS }

export function YesNoStep({
  title, question, description, value, onChange,
  options = DEFAULT_OPTIONS, conditionKey, conditionContent,
}: YesNoStepProps) {
  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-xl font-semibold text-ink mb-1">{title}</h2>
        {description && <p className="text-sm text-ink-3 leading-relaxed">{description}</p>}
      </div>

      <div className="bg-white rounded-2xl border border-sand-300 shadow-soft p-6">
        <p className="text-base font-medium text-ink mb-5">{question}</p>
        <div className="flex flex-col gap-3">
          {options.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              className={cn(
                'flex items-center gap-4 rounded-xl border-2 px-5 py-4 text-left transition-all',
                value === opt.value
                  ? 'border-sage-600 bg-sage-50'
                  : 'border-sand-300 bg-white hover:border-sage-300',
              )}
            >
              {/* Radio-stijl cirkel */}
              <div className={cn(
                'h-5 w-5 rounded-full border-2 flex-shrink-0 transition-colors',
                value === opt.value
                  ? 'border-sage-600 bg-sage-600'
                  : 'border-sand-400 bg-white',
              )} />
              <span className={cn(
                'text-base font-medium',
                value === opt.value ? 'text-sage-800' : 'text-ink-2',
              )}>
                {opt.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {conditionContent && value === conditionKey && (
        <div className="animate-slide-down">
          {conditionContent}
        </div>
      )}
    </div>
  )
}
